import { observer } from 'mobx-react-lite'
import { Navigate } from 'react-router'
import { links } from '@/utils/link'
import { ContentLoading } from '@/components/ContentLoading'
import { type CallCenterJobViewModel } from '@/features/call-center/JobSection/CallCenterJobViewModel'
import { AddCouponDialogContainer } from '@/features/call-center/AddCouponDialog/AddCouponDialogContainer'
import { useActivation } from '@/hooks/view-model-hooks'
import { type AppliedCouponViewModel } from '@/features/jobs/drafting/checkout/JobCheckoutViewModel'
import { type Task } from 'mobx-task'
import {
  CouponDetail,
  JobCheckout,
  PriceBreakdown,
} from '@/features/call-center/JobCheckout'

export interface JobCheckoutContainerProps {
  jobVm: CallCenterJobViewModel
}

export const JobCheckoutContainer = observer(function JobCheckoutContainer({
  jobVm,
}: JobCheckoutContainerProps) {
  const vm = jobVm.jobCheckoutVm

  useActivation(jobVm.checkoutVm)

  if (vm.fetchJobInfo.pending || !vm.job.id) {
    return <ContentLoading size="inline" />
  }

  if (!jobVm.isCheckoutReady) {
    return <Navigate to={links.callCenterJob(vm.id)} replace />
  }

  return (
    <JobCheckout
      contact={{
        fullName: vm.championClient?.member?.displayName ?? '',
        email: vm.championClient?.member?.email ?? '',
        phone: vm.championClient?.member?.phone ?? '',
        workspace: vm.championClient?.member.workspace.name ?? '',
      }}
      amountDueNow={vm.amountDueNow}
      priceBreakdown={
        <PriceBreakdown
          scopeItemNames={vm.quoteScopeItemsNames.slice()}
          hasCoupons={vm.hasCoupons}
          discountLabel={vm.discountLabel}
          couponDetail={
            vm.appliedCouponVm && (
              <CouponDetailContainer
                vm={vm.appliedCouponVm}
                remove={jobVm.removeCoupon}
              />
            )
          }
          primaryAmount={vm.primaryAmount}
          discount={vm.discount}
          total={vm.total}
        />
      }
      showAddCoupon={!vm.hasCoupons}
      onAddCoupon={jobVm.addCouponDialogVm.modal.show}
      checkoutLink={
        !jobVm.canSubmitJob
          ? {
              checkoutLink: jobVm.jobCheckoutVm.checkoutLink,
              isEmailingCheckoutLink: jobVm.isEmailingCheckoutLink,
              onSendEmail: jobVm.emailCheckoutLink,
              onCopy: jobVm.copyCheckoutLink,
            }
          : null
      }
      onPrevious={jobVm.goBackToJob}
      isSubmitPending={jobVm.submitJob.pending}
      canSubmit={!!jobVm.canSubmitJob}
      onSubmit={jobVm.submitJob}
      couponDialog={<AddCouponDialogContainer vm={jobVm.addCouponDialogVm} />}
    />
  )
})

interface CouponDetailContainerProps {
  vm: AppliedCouponViewModel
  remove: Task<[], Promise<void>>
}

const CouponDetailContainer = observer(function CouponDetailContainer({
  vm,
  remove,
}: CouponDetailContainerProps) {
  return (
    <CouponDetail
      name={vm.name}
      description={vm.description}
      discount={vm.discount}
      isRemoving={remove.pending}
      onRemove={remove}
    />
  )
})
